import { useEffect, useRef, useState } from "react";
import { collection, deleteDoc, doc, getDocs, getFirestore } from "firebase/firestore";
import { app, currentSession, signOutNow, resolveName } from "../firebase";
import { getDevMode, setDevMode, setNameOverride, getNameOverride, clearNameOverride } from "../storage";
import { Screen, ScreenHeader, Display, Field, Input, SectionLabel, Btn } from "../ui/components";
import { Ico } from "../ui/Ico";
import { C, mono } from "../ui/tokens";
import { APP_VERSION } from "../version";

const TAPS_TO_UNLOCK = 20;
const TAP_RESET_MS = 2000;

export function Settings({
  employee, email, onBack, onSignedOut, onNameChange,
}: {
  employee: string;
  email: string;
  onBack: () => void;
  onSignedOut: () => void;
  onNameChange: (name: string) => void;
}) {
  const [name, setName] = useState(employee);
  const [savedHint, setSavedHint] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearMsg, setClearMsg] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [hasOverride, setHasOverride] = useState<boolean>(() => !!getNameOverride(email));
  const [devMode, setDevModeState] = useState<boolean>(() => getDevMode());
  const [tapCount, setTapCount] = useState(0);
  const tapTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (tapTimer.current !== null) window.clearTimeout(tapTimer.current);
    };
  }, []);

  function onVersionTap() {
    if (devMode) return;
    const next = tapCount + 1;
    if (next >= TAPS_TO_UNLOCK) {
      setDevMode(true);
      setDevModeState(true);
      setTapCount(0);
      if (tapTimer.current !== null) window.clearTimeout(tapTimer.current);
      return;
    }
    setTapCount(next);
    if (tapTimer.current !== null) window.clearTimeout(tapTimer.current);
    tapTimer.current = window.setTimeout(() => setTapCount(0), TAP_RESET_MS);
  }

  function disableDevMode() {
    setDevMode(false);
    setDevModeState(false);
  }

  function saveName() {
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    setNameOverride(email, trimmed);
    setHasOverride(true);
    setSavedHint(true);
    onNameChange(trimmed);
    setTimeout(() => setSavedHint(false), 1500);
  }

  async function clearHistory() {
    setClearing(true);
    setClearMsg(null);
    try {
      const session = await currentSession();
      if (!session) { setClearMsg("Nicht angemeldet."); return; }
      const db = getFirestore(app);
      const ref = collection(doc(db, "history", session.uid), "items");
      const snap = await getDocs(ref);
      await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
      setClearMsg(`${snap.size} Einträge gelöscht.`);
    } catch (e) {
      console.error(e);
      setClearMsg("Fehler beim Löschen.");
    } finally {
      setClearing(false);
      setConfirming(false);
    }
  }

  return (
    <Screen>
      <ScreenHeader back={onBack} />
      <Display>Einstellungen</Display>

      <div style={{ marginTop: 32 }}>
        <Field
          label="Vorname Nachname"
          hint={savedHint ? "Gespeichert." : "Aus deinem Google-Konto übernommen. Änderungen gelten nur für dieses Gerät."}
        >
          <Input value={name} onChange={setName} />
        </Field>

        <div style={{ display: "flex", gap: 8 }}>
          <Btn kind="glass" onClick={saveName} disabled={name.trim().length === 0 || name.trim() === employee}>
            Namen speichern
          </Btn>
          {hasOverride && (
            <Btn kind="ghost" onClick={async () => {
              clearNameOverride();
              setHasOverride(false);
              const session = await currentSession();
              const resolved = session ? resolveName(session) : "";
              setName(resolved);
              onNameChange(resolved);
            }}>
              Auf Google-Namen zurücksetzen
            </Btn>
          )}
        </div>

        <SectionLabel style={{ marginTop: 32 }}>Konto</SectionLabel>
        <div style={{
          padding: "14px 16px", background: C.surface,
          border: `1px solid ${C.border}`, borderRadius: 16,
        }}>
          <div style={{
            fontSize: 11, color: C.fg3, letterSpacing: "0.18em",
            textTransform: "uppercase", marginBottom: 6,
          }}>Angemeldet als</div>
          <div style={{ fontFamily: mono, fontSize: 13, color: C.fg, wordBreak: "break-all" }}>{email}</div>
        </div>
        <div style={{ marginTop: 12 }}>
          <Btn kind="ghost" onClick={async () => { await signOutNow(); onSignedOut(); }}>
            Abmelden
          </Btn>
        </div>

        <SectionLabel style={{ marginTop: 32 }}>Daten</SectionLabel>

        {!confirming ? (
          <button
            onClick={() => setConfirming(true)}
            style={{
              width: "100%",
              padding: "16px 18px",
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              color: C.fg,
              display: "flex", alignItems: "center", gap: 14, textAlign: "left",
              cursor: "pointer",
            }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: 12,
              background: C.dangerBg,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Ico name="trash" size={18} color={C.danger} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 500 }}>Letzte Einreichungen löschen</div>
              <div style={{ fontSize: 12, color: C.fg3, marginTop: 2 }}>
                Lokaler Verlauf auf diesem Gerät. Die Daten in Sheet und Drive bleiben.
              </div>
            </div>
            <Ico name="chev" size={16} color={C.fg4} />
          </button>
        ) : (
          <div style={{
            padding: 16, borderRadius: 16,
            background: C.dangerBg,
            border: "1px solid rgba(255,90,90,0.22)",
          }}>
            <div style={{ fontSize: 14, color: C.fg, marginBottom: 12 }}>
              Wirklich alle lokalen Einträge löschen?
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn kind="danger" onClick={clearHistory} disabled={clearing}>
                {clearing ? "Lösche…" : "Löschen"}
              </Btn>
              <Btn kind="ghost" onClick={() => setConfirming(false)} disabled={clearing}>
                Abbrechen
              </Btn>
            </div>
          </div>
        )}

        {clearMsg && (
          <div style={{
            marginTop: 12, padding: "10px 14px",
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 12, fontSize: 13, color: C.fg2,
          }}>
            {clearMsg}
          </div>
        )}

        {devMode && (
          <>
            <SectionLabel style={{ marginTop: 32 }}>Entwicklermodus</SectionLabel>
            <div style={{
              padding: "14px 16px",
              background: C.surface, border: `1px solid ${C.border}`,
              borderRadius: 16, color: C.fg,
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 500 }}>Entwicklermodus</div>
                <div style={{ fontSize: 12, color: C.fg3, marginTop: 2 }}>
                  Einzelne Einträge in „Letzte Einreichungen" löschbar.
                </div>
              </div>
              <button
                onClick={disableDevMode}
                style={{
                  fontFamily: mono, fontSize: 11, letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  padding: "8px 14px", borderRadius: 10,
                  background: C.surface2, color: C.fg2,
                  border: `1px solid ${C.border}`, cursor: "pointer",
                }}
              >
                Aus
              </button>
            </div>
          </>
        )}

        <button
          onClick={onVersionTap}
          style={{
            marginTop: 40, width: "100%", textAlign: "center",
            background: "transparent", border: "none", padding: 0,
            cursor: devMode ? "default" : "pointer",
            fontFamily: mono, fontSize: 11,
            color: devMode
              ? C.fg3
              : tapCount >= 7
                ? `rgba(255,255,255,${0.28 + (tapCount / TAPS_TO_UNLOCK) * 0.5})`
                : C.fg4,
            letterSpacing: "0.12em",
            transition: "color 120ms ease",
          }}
        >
          SPESEN · v{APP_VERSION}{devMode ? " · DEV" : ""}
        </button>
      </div>
    </Screen>
  );
}
